import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, Download, Loader2, ShieldCheck } from "lucide-react";
import JurisdictionSelect, { TurkiyeNotice } from "../components/JurisdictionSelect";

const SCOPE_OPTIONS = ["GDPR", "AI Act", "DORA", "NIS2", "DSA", "DMA", "Data Act", "CSRD", "MiCA"];
const TR_SCOPE_OPTIONS = [
    "KVKK (Law No. 6698)",
    "Turkish Commercial Code (Law No. 6102)",
    "Code of Obligations (Law No. 6098)",
    "Competition Law (Law No. 4054)",
    "Banking Law (Law No. 5411)",
    "Electronic Communications Law (Law No. 5809)",
    "Capital Markets Law (Law No. 6362)",
    "E-Commerce Law (Law No. 6563)",
];
const STEPS = ["Profile", "Scope", "Activities", "Scenario", "Report"];

const ORG_TYPES = [
    "SaaS / B2B software",
    "Consumer platform",
    "Fintech / payments",
    "Credit institution / bank",
    "Insurance",
    "Healthcare / MedTech",
    "Retail / E-commerce",
    "Manufacturing",
    "Public sector / authority",
    "Education / EdTech",
    "Energy / Utilities",
    "Telecoms",
    "Legal services",
    "Other",
];

const JURISDICTIONS = [
    "Austria","Belgium","Bulgaria","Croatia","Cyprus","Czechia","Denmark","Estonia",
    "Finland","France","Germany","Greece","Hungary","Ireland","Italy","Latvia",
    "Lithuania","Luxembourg","Malta","Netherlands","Poland","Portugal","Romania",
    "Slovakia","Slovenia","Spain","Sweden","Türkiye","EEA (Norway/Iceland/Liechtenstein)",
    "Multi-jurisdictional (EU-wide)","Non-EU with EU operations",
];

const SECTORS = [
    "Consumer","B2B","Public interest","Critical infrastructure",
    "Financial services","Healthcare","Education","Employment / HR",
    "Marketing / Advertising","Research & academia",
];

const HEADCOUNT_BANDS = [
    "< 10","10-49","50-249","250-999","1,000-4,999","5,000+",
];

const REVENUE_BANDS = [
    "< €2m","€2m-10m","€10m-50m","€50m-250m","€250m-1bn","€1bn+",
];

const PROCESSING_ACTIVITIES = [
    "Personal data of EU residents",
    "Special-category data (health, biometric, etc.)",
    "Children's data (under 16)",
    "Automated decision-making about individuals",
    "AI / machine-learning models in production",
    "GPAI / foundation models",
    "Content moderation on user-generated content",
    "Payment processing",
    "International data transfers to third countries",
    "Cloud-hosted personal data",
    "ICT third-party dependencies (critical services)",
    "Employee monitoring",
    "Digital advertising / behavioural targeting",
    "Cross-border access by non-EU staff",
];

const RISK_LEVELS = [
    { key: "unclear", label: "Not yet assessed" },
    { key: "low", label: "Low: routine processing" },
    { key: "medium", label: "Medium: some sensitive categories" },
    { key: "high", label: "High: large-scale or sensitive processing" },
    { key: "critical", label: "Critical: regulator scrutiny likely" },
];

export default function ComplianceChecker() {
    const [step, setStep] = useState(0);
    const [profile, setProfile] = useState({
        organisation_type: "",
        jurisdiction: "",
        sector: "",
        headcount: "",
        annual_revenue_eur: "",
        has_eu_operations: true,
    });
    const [scope, setScope] = useState([]);
    const [lawJurisdiction, setLawJurisdiction] = useState("eu");
    const [activities, setActivities] = useState([]);
    const [riskLevel, setRiskLevel] = useState("unclear");
    const [scenario, setScenario] = useState("");
    const [report, setReport] = useState(null);
    const [assessmentId, setAssessmentId] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const canNext =
        (step === 0 && profile.organisation_type && profile.jurisdiction && profile.sector) ||
        (step === 1 && scope.length > 0) ||
        (step === 2 && activities.length > 0) ||
        (step === 3 && scenario.trim().length >= 10);

    const submit = async () => {
        setLoading(true);
        setError("");
        try {
            const composedScenario = [
                scenario.trim(),
                "",
                "Processing activities selected: " + (activities.join("; ") || "none"),
                "Self-assessed risk: " + riskLevel,
            ].join("\n");
            const { data } = await api.post("/compliance/assess", {
                profile,
                scope,
                scenario: composedScenario,
                jurisdiction: lawJurisdiction,
            });
            // Async job: poll until status != "pending"
            const id = data.id;
            setAssessmentId(id);
            for (let i = 0; i < 90; i++) {
                await new Promise((r) => setTimeout(r, 2500));
                const { data: doc } = await api.get(`/compliance/assessments/${id}`);
                if (doc.status === "ready") {
                    setReport(doc.report);
                    setStep(4);
                    setLoading(false);
                    return;
                }
                if (doc.status === "failed") {
                    setError(doc.error || "Assessment failed.");
                    setLoading(false);
                    return;
                }
            }
            setError("Timed out waiting for the assessment. It may still complete. Check Past assessments.");
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Assessment failed.");
        } finally {
            setLoading(false);
        }
    };

    const toggleScope = (s) =>
        setScope((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : cur.length < 10 ? [...cur, s] : cur));

    return (
        <div className="container-edit py-10 lg:py-14 max-w-4xl" data-testid="compliance-checker">
            <div className="label-eyebrow">Module</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">Compliance Checker</h1>
            <p className="mt-3 text-[#0D1B3E]/75 max-w-2xl">
                Walk through your operation in five steps. The platform produces a
                structured assessment with applicable law, obligations, risks, and
                prioritised actions. 2 credits per assessment.
            </p>

            <ol className="mt-8 flex items-center gap-2 flex-wrap" data-testid="compliance-steps">
                {STEPS.map((s, i) => (
                    <li key={s} className="flex items-center gap-2">
                        <span
                            className={`w-7 h-7 grid place-items-center text-xs font-semibold ${
                                i < step
                                    ? "bg-[#C9A84C] text-[#0D1B3E]"
                                    : i === step
                                      ? "bg-[#0D1B3E] text-[#F5F0E8]"
                                      : "border border-[#0D1B3E]/30 text-[#0D1B3E]/50"
                            }`}
                        >
                            {i < step ? <CheckCircle2 size={14} /> : i + 1}
                        </span>
                        <span className={`text-xs uppercase tracking-[0.2em] ${i === step ? "text-[#0D1B3E]" : "text-[#0D1B3E]/55"}`}>
                            {s}
                        </span>
                        {i < STEPS.length - 1 && <span className="w-6 h-px bg-[#0D1B3E]/20 mx-1" />}
                    </li>
                ))}
            </ol>

            <div className="mt-8 card-flat p-7">
                {step === 0 && (
                    <div data-testid="compliance-step-profile">
                        <div className="mb-5 flex flex-wrap items-center gap-3">
                            <JurisdictionSelect
                                value={lawJurisdiction}
                                onChange={(v) => {
                                    setLawJurisdiction(v);
                                    setScope([]);
                                }}
                                testId="compliance-jurisdiction"
                                label="Legal framework"
                            />
                        </div>
                        <TurkiyeNotice jurisdiction={lawJurisdiction} className="mb-5 -mt-2 max-w-2xl" />
                        <div className="grid gap-4 sm:grid-cols-2">
                        <Select label="Organisation type" value={profile.organisation_type}
                                onChange={(v) => setProfile({ ...profile, organisation_type: v })}
                                testId="compliance-input-orgtype" options={ORG_TYPES}
                                placeholder="Select organisation type" />
                        <Select label="Primary jurisdiction" value={profile.jurisdiction}
                                onChange={(v) => setProfile({ ...profile, jurisdiction: v })}
                                testId="compliance-input-jurisdiction" options={JURISDICTIONS}
                                placeholder="Select jurisdiction" />
                        <Select label="Sector" value={profile.sector}
                                onChange={(v) => setProfile({ ...profile, sector: v })}
                                testId="compliance-input-sector" options={SECTORS}
                                placeholder="Select sector" />
                        <Select label="Headcount" value={profile.headcount}
                                onChange={(v) => setProfile({ ...profile, headcount: v })}
                                testId="compliance-input-headcount" options={HEADCOUNT_BANDS}
                                placeholder="Select band" />
                        <Select label="Annual revenue" value={profile.annual_revenue_eur}
                                onChange={(v) => setProfile({ ...profile, annual_revenue_eur: v })}
                                testId="compliance-input-revenue" options={REVENUE_BANDS}
                                placeholder="Select band" />
                        <label className="block">
                            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                                EU operations
                            </span>
                            <div className="flex gap-2">
                                {["yes", "no"].map((v) => {
                                    const on = (v === "yes") === profile.has_eu_operations;
                                    return (
                                        <button
                                            key={v}
                                            type="button"
                                            onClick={() => setProfile({ ...profile, has_eu_operations: v === "yes" })}
                                            className={`min-h-[44px] px-4 border text-sm ${
                                                on
                                                    ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                                    : "border-[#0D1B3E]/20 text-[#0D1B3E] hover:bg-[#0D1B3E]/5"
                                            }`}
                                            data-testid={`compliance-input-euops-${v}`}
                                        >
                                            {v === "yes" ? "Active in the EU" : "Not active in the EU"}
                                        </button>
                                    );
                                })}
                            </div>
                        </label>
                        </div>
                    </div>
                )}
                {step === 1 && (
                    <div data-testid="compliance-step-scope">
                        <p className="text-sm text-[#0D1B3E]/75 mb-4">
                            {lawJurisdiction === "eu"
                                ? "Choose the EU instruments you want assessed."
                                : lawJurisdiction === "turkiye"
                                  ? "Choose the Turkish instruments you want assessed."
                                  : "Choose the EU and Turkish instruments you want assessed side by side."}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {(lawJurisdiction === "eu"
                                ? SCOPE_OPTIONS
                                : lawJurisdiction === "turkiye"
                                  ? TR_SCOPE_OPTIONS
                                  : [...SCOPE_OPTIONS, ...TR_SCOPE_OPTIONS]
                            ).map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => toggleScope(s)}
                                    data-testid={`compliance-scope-${s.replace(/\s+/g, "-")}`}
                                    className={`min-h-[44px] px-4 text-sm border transition-colors ${
                                        scope.includes(s)
                                            ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                            : "border-[#0D1B3E]/20 text-[#0D1B3E] hover:bg-[#0D1B3E]/5"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {step === 2 && (
                    <div data-testid="compliance-step-activities">
                        <p className="text-sm text-[#0D1B3E]/75 mb-4">
                            Which processing activities apply? Pick all that match. This
                            drives which obligations attach.
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                            {PROCESSING_ACTIVITIES.map((a) => {
                                const on = activities.includes(a);
                                return (
                                    <button
                                        key={a}
                                        type="button"
                                        onClick={() =>
                                            setActivities((cur) =>
                                                cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a],
                                            )
                                        }
                                        data-testid={`compliance-activity-${a.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                        className={`text-left min-h-[44px] px-4 py-2 text-sm border transition-colors ${
                                            on
                                                ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                                : "border-[#0D1B3E]/20 text-[#0D1B3E] hover:bg-[#0D1B3E]/5"
                                        }`}
                                    >
                                        {on ? "✓ " : ""}{a}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-6">
                            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                                Self-assessed risk (optional)
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {RISK_LEVELS.map((r) => (
                                    <button
                                        key={r.key}
                                        type="button"
                                        onClick={() => setRiskLevel(r.key)}
                                        data-testid={`compliance-risk-${r.key}`}
                                        className={`min-h-[44px] px-4 text-sm border ${
                                            riskLevel === r.key
                                                ? "bg-[#C9A84C] text-[#0D1B3E] border-[#C9A84C]"
                                                : "border-[#0D1B3E]/20 text-[#0D1B3E] hover:bg-[#0D1B3E]/5"
                                        }`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {step === 3 && (
                    <div data-testid="compliance-step-scenario">
                        <label className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                            Describe the operation, product or process
                        </label>
                        <p className="text-xs text-[#0D1B3E]/60 mb-3">
                            Optional supplement to the structured inputs above. Free text is
                            merged into the assessment context.
                        </p>
                        <textarea
                            rows={9}
                            className="field"
                            placeholder="e.g. We're an EU SaaS deploying a content-moderation model on user-submitted text. Data is processed in Frankfurt; some support staff in the US access live customer data via a screen-sharing tool."
                            value={scenario}
                            onChange={(e) => setScenario(e.target.value)}
                            data-testid="compliance-input-scenario"
                        />
                    </div>
                )}
                {step === 4 && report && <ReportView report={report} assessmentId={assessmentId} />}

                {error && (
                    <p className="mt-4 text-sm text-red-700" data-testid="compliance-error">{error}</p>
                )}
            </div>

            <div className="mt-6 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0 || loading}
                    className="btn-secondary disabled:opacity-40 min-h-[44px]"
                    data-testid="compliance-back"
                >
                    <ArrowLeft size={14} /> Back
                </button>
                {step < 3 && (
                    <button
                        type="button"
                        onClick={() => setStep((s) => s + 1)}
                        disabled={!canNext}
                        className="btn-primary disabled:opacity-50 min-h-[44px]"
                        data-testid="compliance-next"
                    >
                        Next <ArrowRight size={14} />
                    </button>
                )}
                {step === 3 && (
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canNext || loading}
                        className="btn-primary disabled:opacity-50 min-h-[44px]"
                        data-testid="compliance-submit"
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                        Generate report
                    </button>
                )}
                {step === 4 && (
                    <button
                        type="button"
                        onClick={() => { setStep(0); setReport(null); setScenario(""); setActivities([]); setRiskLevel("unclear"); }}
                        className="btn-secondary min-h-[44px]"
                        data-testid="compliance-restart"
                    >
                        Start a new assessment
                    </button>
                )}
            </div>
        </div>
    );
}

function Select({ label, value, onChange, options, testId, placeholder }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">{label}</span>
            <select
                className="field min-h-[44px]"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
            >
                <option value="">{placeholder || "Select…"}</option>
                {options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                ))}
            </select>
        </label>
    );
}

function Field({ label, value, onChange, testId, placeholder }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">{label}</span>
            <input type="text" className="field min-h-[44px]" value={value} placeholder={placeholder}
                   onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        </label>
    );
}

const SEVERITY_CLR = { high: "text-red-700", medium: "text-amber-700", low: "text-emerald-700", critical: "text-red-800" };

function ReportView({ report, assessmentId }) {
    const [exporting, setExporting] = useState(false);
    const [exportErr, setExportErr] = useState("");

    async function exportDocx() {
        if (!assessmentId) return;
        try {
            setExporting(true);
            setExportErr("");
            const res = await api.get(
                `/exports/compliance/${assessmentId}.docx`,
                { responseType: "blob" },
            );
            const blob = new Blob([res.data]);
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `compliance-${assessmentId.slice(0, 8)}.docx`;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                URL.revokeObjectURL(link.href);
                link.remove();
            }, 500);
        } catch (e) {
            const d = e?.response?.data;
            let msg = "Export failed";
            if (typeof d === "string") msg = d;
            else if (d?.detail) msg = typeof d.detail === "string" ? d.detail : (d.detail.message || msg);
            setExportErr(msg);
        } finally {
            setExporting(false);
        }
    }

    return (
        <div data-testid="compliance-report" className="space-y-7">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Headline risk</div>
                    <p className="font-display text-2xl mt-2 leading-snug" data-testid="compliance-headline">
                        {report.headline_risk}
                    </p>
                    <div className={`mt-2 text-xs uppercase tracking-[0.22em] ${SEVERITY_CLR[report.overall_risk_level] || ""}`}
                         data-testid="compliance-overall-risk">
                        Overall risk: {report.overall_risk_level}
                    </div>
                </div>
                {assessmentId && (
                    <div className="flex flex-col items-end gap-1">
                        <button
                            type="button"
                            onClick={exportDocx}
                            disabled={exporting}
                            className="btn-secondary min-h-[44px]"
                            data-testid="compliance-export-docx"
                        >
                            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            Export to Word
                        </button>
                        {exportErr && (
                            <span className="text-xs text-red-700 max-w-xs text-right">{exportErr}</span>
                        )}
                    </div>
                )}
            </div>

            <Section title="Applicable law">
                <ul className="space-y-3" data-testid="compliance-applicable-law">
                    {(report.applicable_law || []).map((a, i) => (
                        <li key={i} className="border-l-2 border-[#C9A84C] pl-3">
                            <div className="font-display text-lg">{a.instrument}</div>
                            {a.key_articles?.length > 0 && (
                                <div className="text-[11px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                                    {a.key_articles.join(" · ")}
                                </div>
                            )}
                            <p className="text-sm mt-1 text-[#0D1B3E]/85">{a.why}</p>
                        </li>
                    ))}
                </ul>
            </Section>

            <Section title="Obligations">
                <ul className="space-y-2" data-testid="compliance-obligations">
                    {(report.obligations || []).map((o, i) => (
                        <li key={i} className="text-sm">
                            <span className={`text-[10px] uppercase tracking-[0.18em] mr-2 ${SEVERITY_CLR[o.severity] || ""}`}>
                                {o.severity}
                            </span>
                            <span className="font-semibold">{o.area}.</span> {o.obligation}
                            {o.primary_source && (
                                <span className="text-[#0D1B3E]/55"> [{o.primary_source}]</span>
                            )}
                        </li>
                    ))}
                </ul>
            </Section>

            <Section title="Risks">
                <ul className="space-y-3" data-testid="compliance-risks">
                    {(report.risks || []).map((r, i) => (
                        <li key={i} className="card-flat p-4">
                            <p className="text-sm">{r.risk}</p>
                            <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                                Likelihood {r.likelihood} · Impact {r.impact}
                            </div>
                            {r.mitigations?.length > 0 && (
                                <ul className="mt-2 list-disc pl-5 text-sm text-[#0D1B3E]/85">
                                    {r.mitigations.map((m, j) => <li key={j}>{m}</li>)}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            </Section>

            <Section title="Recommended actions">
                <ul className="space-y-2" data-testid="compliance-actions">
                    {(report.recommended_actions || []).map((a, i) => (
                        <li key={i} className="text-sm flex items-start gap-3">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9A84C] font-semibold">
                                {a.priority}
                            </span>
                            <span className="flex-1">{a.action}</span>
                            <span className="text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                                {a.timeframe}
                            </span>
                        </li>
                    ))}
                </ul>
            </Section>

            {report.assumptions?.length > 0 && (
                <Section title="Assumptions">
                    <ul className="list-disc pl-5 text-sm text-[#0D1B3E]/75">
                        {report.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                </Section>
            )}

            <div className="flex flex-wrap gap-3">
                <Link to="/app/research" className="btn-secondary">Open in Research Assistant</Link>
                <Link to="/app/argument" className="btn-secondary">Draft a legal argument</Link>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <div className="label-eyebrow">{title}</div>
            <div className="mt-3">{children}</div>
        </section>
    );
}
